import { Link } from "react-router-dom";
import { Target, Eye, Award, Users, ArrowRight } from "lucide-react";
import { usePageMeta } from "@/hooks/usePageMeta";

const values = [
  { icon: Target, title: "Innovation", desc: "We stay at the forefront of technology, constantly exploring new solutions to deliver better results." },
  { icon: Eye, title: "Transparency", desc: "Clear communication and honest partnerships form the foundation of every client relationship." },
  { icon: Award, title: "Excellence", desc: "We hold ourselves to the highest standards, delivering quality solutions that exceed expectations." },
  { icon: Users, title: "Client-First", desc: "Every decision we make is guided by the success and satisfaction of our clients." },
];

const milestones = [
  { year: "2014", event: "Company Founded" },
  { year: "2016", event: "First Enterprise ERP Deployment" },
  { year: "2018", event: "Cloud Services Division Launched" },
  { year: "2020", event: "Cyber Security Practice Established" },
  { year: "2022", event: "Expanded to 50+ Enterprise Clients" },
  { year: "2024", event: "200+ Projects Successfully Delivered" },
];

export default function AboutPage() {
  usePageMeta("About Us", "Vaana BuildLogic Technologies is a New Delhi-based IT company specializing in ERP, cloud, cybersecurity, AR/VR, AI/ML, IoT, and AMC/CMC services. With 200+ projects and 50+ enterprise clients, we deliver secure, scalable digital solutions.");
  return (
    <div data-testid="about-page" className="pt-20">
      {/* Hero */}
      <section className="relative py-24 md:py-32 wave-divider overflow-hidden">
        <div className="absolute inset-0 bg-[#0D72B8]" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-blue-200 font-semibold mb-4">About Us</p>
              <h1 className="text-5xl sm:text-6xl leading-tight font-['Sora'] font-medium text-white mb-6">
                A decade of excellence in enterprise IT and digital innovation.
              </h1>
              <p className="text-blue-100 leading-relaxed mb-4">
                Vaana BuildLogic Technologies is a New Delhi-based IT company specializing in ERP, cloud, cybersecurity, AR/VR, AI/ML, IoT, and AMC/CMC services.
              </p>
              <p className="text-blue-100 leading-relaxed">
                With 200+ projects delivered and 50+ enterprise clients, we empower businesses with secure, scalable digital solutions.
              </p>
            </div>
            <div className="hidden lg:block">
              <img src="https://images.pexels.com/photos/7964413/pexels-photo-7964413.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Our team" className="rounded-2xl shadow-2xl w-full object-cover h-80" />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div data-testid="mission-card" className="p-10 bg-white border border-gray-200 rounded-2xl card-clean">
              <div className="w-14 h-14 flex items-center justify-center mb-6">
                <Target size={32} className="text-[#0D72B8]" strokeWidth={1.5} />
              </div>
              <h3 className="text-2xl font-['Sora'] font-bold text-[#111827] mb-4">Our Mission</h3>
              <p className="text-[#6B7280] leading-relaxed">
                To empower organizations with innovative technology solutions that enhance operational efficiency, strengthen security, and accelerate growth with a personalized approach.
              </p>
            </div>
            <div data-testid="vision-card" className="p-10 bg-white border border-gray-200 rounded-2xl card-clean">
              <div className="w-14 h-14 flex items-center justify-center mb-6">
                <Eye size={32} className="text-[#0D72B8]" strokeWidth={1.5} />
              </div>
              <h3 className="text-2xl font-['Sora'] font-bold text-[#111827] mb-4">Our Vision</h3>
              <p className="text-[#6B7280] leading-relaxed">
                To become India's most trusted technology partner, known for transforming businesses through secure, scalable, and intelligent IT infrastructure.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-24 md:py-32 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-['Sora'] font-semibold text-[#111827]">Core Values</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {values.map((v, i) => (
              <div key={i} data-testid={`value-card-${i}`} className="text-center card-clean">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-5">
                  <v.icon size={30} className="text-[#0D72B8]" strokeWidth={1.5} />
                </div>
                <h4 className="text-xl font-['Sora'] font-semibold text-[#111827] mb-2">{v.title}</h4>
                <p className="text-sm text-[#6B7280] leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-['Sora'] font-semibold text-[#111827]">Milestones</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {milestones.map((m, i) => (
              <div key={i} data-testid={`milestone-${i}`} className="flex items-center gap-5 p-6 border border-gray-200 rounded-xl card-clean">
                <span className="text-3xl font-['Sora'] font-bold text-[#0D72B8] flex-shrink-0">{m.year}</span>
                <p className="text-sm text-[#6B7280]">{m.event}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0D72B8] py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 text-center">
          <h2 className="text-4xl sm:text-5xl font-['Sora'] font-semibold text-white mb-6">
            Let's build something great together.
          </h2>
          <p className="text-blue-100 max-w-xl mx-auto mb-10">Partner with us to bring your digital vision to life.</p>
          <Link to="/contact" data-testid="about-cta-button"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-[#0D72B8] font-semibold text-sm rounded-full hover:bg-gray-50 transition-all shadow-lg">
            Contact Us <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
